import {
  Component,
  ViewEncapsulation,
  AfterContentInit,
  Input,
  ContentChildren,
  QueryList,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { trigger, state, style, transition, animate } from "@angular/animations";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MatTableDataSource } from "@angular/material/table";
import { MatSort } from "@angular/material/sort";
import { DsColumnComponent } from "./ds-column/ds-column.component";
import { DsExpandRowComponent } from "./ds-expand-row/ds-expand-row.component";
import { SortDirection, Sizes } from "../types/table.types";

const EXPAND_TOGGLE_COLUMN = "__dsExpandToggle";

interface DsTableRow extends Record<string, unknown> {
  __rowType?: "data" | "nested";
  __sourceRow?: Record<string, unknown>;
  __parentRow?: Record<string, unknown>;
  __nestedIndex?: number;
  __hasExpandableContent?: boolean;
  __isClickable?: boolean;
  __isExpanded?: boolean;
  __expandIcon?: "up-arrow" | "down-arrow" | null;
  __expandState?: "expanded" | "collapsed";
  __showExpandDetail?: boolean;
  __isOdd?: boolean;
}

@Component({
  selector: "ds-table",
  templateUrl: "./ds-table.component.html",
  styleUrls: ["./ds-table.component.scss"],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger("expandRow", [
      state("collapsed", style({ height: "0px", opacity: 0, overflow: "hidden" })),
      state("expanded", style({ height: "*", opacity: 1 })),
      transition("collapsed => expanded", animate("150ms ease-out")),
      transition("expanded => collapsed", animate("150ms ease-in")),
    ]),
  ],
})
export class DsTableComponent implements AfterContentInit, OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() dataSource: any[] = [];
  @Input() trackBy: any;
  @Input() initialSortActive = "";
  @Input() initialSortDirection: SortDirection = "";
  @Input() sortDisableClear = false;
  @Input() stripedRows = false;
  @Input() size: Sizes = "sm";
  @Input() focusable = false;
  @Input() headerRows: string[][] | null = null;
  @Input() noDataLabel = "No se encontraron datos";
  @Output() sortChange = new EventEmitter<any>();
  @Output() contentChanged = new EventEmitter<void>();
  // Emits the source row when a data row is clicked (independent of expand toggling).
  @Output() rowClick = new EventEmitter<Record<string, unknown>>();

  @ViewChild(MatSort) private matSort?: MatSort;
  @ContentChildren(DsColumnComponent) columns!: QueryList<DsColumnComponent>;
  @ContentChildren(DsExpandRowComponent) expandRows!: QueryList<DsExpandRowComponent>;

  tableData = new MatTableDataSource<DsTableRow>();
  displayedColumns: string[] = [];
  firstHeaderRow: string[] = [];
  secondHeaderRow: string[] = [];
  expandColumns: string[] = [];
  private expandedRows = new Set<Record<string, unknown>>();
  private initialOpenAppliedFor = new WeakSet<Record<string, unknown>>();
  hasExpandRows = false;

  columnList: DsColumnComponent[] = [];
  expandList: DsExpandRowComponent[] = [];

  readonly EXPAND_TOGGLE_COLUMN = EXPAND_TOGGLE_COLUMN;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["dataSource"]) {
      const previousData = changes["dataSource"].previousValue as Record<string, unknown>[] | undefined;
      this.syncExpandedRows(previousData, changes["dataSource"].firstChange);
      this.rebuildTableData();
    }

    if (changes["stripedRows"]) {
      this.rebuildTableData();
    }

    if (changes["headerRows"] && !changes["headerRows"].firstChange) {
      this.updateColumns();
    }

    this.cdr.detectChanges();
  }

  ngAfterContentInit(): void {
    this.initializeTable();
    this.columns.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateColumns();
    });
    this.expandRows.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateExpandRows();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackColumnName(_index: number, column: DsColumnComponent): string {
    return column.name;
  }

  private initializeTable(): void {
    this.updateColumns();
    this.updateExpandRows();
    this.cdr.detectChanges();
  }

  private rebuildTableData(): void {
    const rows: DsTableRow[] = [];
    let flatIndex = 0;

    for (const row of this.dataSource || []) {
      const isExpanded = this.expandedRows.has(row);
      const hasExpandableContent = this.computeHasExpandableContent(row);

      rows.push({
        ...row,
        __rowType: "data",
        __sourceRow: row,
        __hasExpandableContent: hasExpandableContent,
        __isClickable: this.hasExpandRows && hasExpandableContent,
        __isExpanded: isExpanded,
        __expandIcon: hasExpandableContent ? (isExpanded ? "up-arrow" : "down-arrow") : null,
        __expandState: isExpanded ? "expanded" : "collapsed",
        __showExpandDetail: isExpanded && this.computeHasStringExpandContent(row),
        __isOdd: this.computeIsOddRow(flatIndex),
      });
      flatIndex++;

      if (isExpanded) {
        for (const expand of this.expandList) {
          const value = row[expand.name];

          if (Array.isArray(value)) {
            value.forEach((nestedRow, nestedIndex) => {
              rows.push({
                ...(nestedRow as Record<string, unknown>),
                __rowType: "nested",
                __sourceRow: nestedRow as Record<string, unknown>,
                __parentRow: row,
                __nestedIndex: nestedIndex,
                __hasExpandableContent: false,
                __isClickable: false,
                __isExpanded: false,
                __expandIcon: null,
                __expandState: "collapsed",
                __showExpandDetail: false,
                __isOdd: this.computeIsOddRow(flatIndex),
              });
              flatIndex++;
            });
          }
        }
      }
    }

    this.tableData.data = rows;
  }

  private updateColumns(): void {
    this.columnList = this.columns.toArray();
    this.displayedColumns = this.buildDisplayedColumns();
    this.updateHeaderRowDefs();
    this.cdr.detectChanges();
  }

  private buildDisplayedColumns(): string[] {
    const bodyColumns = this.columnList.filter((column) => !column.headerOnly).map((column) => column.name);
    const headerNames = this.headerRows?.length ? new Set(this.headerRows.flat()) : null;
    const columns = headerNames ? bodyColumns.filter((name) => headerNames.has(name)) : bodyColumns;

    if (this.hasExpandRows) {
      return [...columns, EXPAND_TOGGLE_COLUMN];
    }

    return columns;
  }

  private updateHeaderRowDefs(): void {
    const definedNames = new Set(this.columnList.map((column) => column.name));

    if (this.headerRows?.length) {
      this.firstHeaderRow = (this.headerRows[0] ?? []).filter((name) => definedNames.has(name));
      this.secondHeaderRow = (this.headerRows[1] ?? []).filter((name) => definedNames.has(name));
    } else {
      this.firstHeaderRow = [...this.displayedColumns];
      this.secondHeaderRow = [];
    }

    this.syncExpandToggleHeaderColumn();
  }
  private syncExpandToggleHeaderColumn(): void {
    this.firstHeaderRow = this.firstHeaderRow.filter((name) => name !== EXPAND_TOGGLE_COLUMN);
    this.secondHeaderRow = this.secondHeaderRow.filter((name) => name !== EXPAND_TOGGLE_COLUMN);

    if (!this.hasExpandRows) {
      return;
    }

    this.firstHeaderRow = [...this.firstHeaderRow, EXPAND_TOGGLE_COLUMN];
  }

  private updateExpandRows(): void {
    this.expandList = this.expandRows.toArray();
    this.expandColumns = this.expandList.map((r) => r.name);
    this.hasExpandRows = this.expandList.length > 0;
    this.displayedColumns = this.buildDisplayedColumns();
    this.updateHeaderRowDefs();
    this.applyInitialOpenRows();
    this.rebuildTableData();
    this.cdr.detectChanges();
  }

  private syncExpandedRows(previousData: Record<string, unknown>[] | undefined, isFirstChange: boolean): void {
    const data = this.dataSource || [];

    this.expandedRows = new Set([...this.expandedRows].filter((row) => data.includes(row)));

    if (isFirstChange || !previousData?.length) {
      this.expandedRows.clear();
      this.applyInitialOpenRows();
      return;
    }

    for (const row of data) {
      if (!previousData.includes(row)) {
        this.applyInitialOpenForRow(row);
      }
    }
  }

  private applyInitialOpenRows(): void {
    for (const row of this.dataSource || []) {
      this.applyInitialOpenForRow(row);
    }
  }

  private applyInitialOpenForRow(row: Record<string, unknown>): void {
    if (!row.initial_open || this.initialOpenAppliedFor.has(row)) {
      return;
    }

    if (!this.computeHasExpandableContent(row)) {
      return;
    }

    this.expandedRows.add(row);
    this.initialOpenAppliedFor.add(row);
  }

  onRowClick(row: DsTableRow, event?: Event): void {
    this.rowClick.emit((row.__sourceRow ?? row) as Record<string, unknown>);

    if (!row.__isClickable) {
      return;
    }

    this.toggleExpand(row, event);
  }

  onFocusableActivate(row: DsTableRow, event: Event, preventDefault = false): void {
    if (!this.focusable) {
      return;
    }

    const target = event.target as HTMLElement;
    if (this.isEditableTarget(target)) {
      return;
    }

    if (preventDefault) {
      event.preventDefault();
    }

    this.onRowClick(row, event);
  }

  onArrowNavigate(event: KeyboardEvent, direction: number): void {
    if (!this.focusable) {
      return;
    }

    const target = event.target as HTMLElement;
    if (this.isEditableTarget(target)) {
      return;
    }

    const currentRow = event.currentTarget as HTMLElement;
    const rows = Array.from(currentRow.closest("table")?.querySelectorAll<HTMLElement>("tbody tr[tabindex]") ?? []);
    const nextRow = rows[rows.indexOf(currentRow) + direction];
    if (!nextRow) {
      return;
    }

    event.preventDefault();
    nextRow.focus();
  }

  private isEditableTarget(target: HTMLElement): boolean {
    const tagName = target.tagName;
    return tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT" || target.isContentEditable;
  }

  toggleExpand(row: DsTableRow, event?: Event): void {
    event?.stopPropagation();

    const sourceRow = row.__sourceRow ?? row;

    if (!row.__hasExpandableContent) {
      return;
    }

    if (this.expandedRows.has(sourceRow)) {
      this.expandedRows.delete(sourceRow);
    } else {
      this.expandedRows.add(sourceRow);
    }

    this.rebuildTableData();
    this.cdr.detectChanges();
  }

  private computeHasExpandableContent(sourceRow: Record<string, unknown>): boolean {
    return this.expandList.some((expand) => {
      const value = sourceRow[expand.name];

      if (value == null || value === "") {
        return false;
      }

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return true;
    });
  }

  private computeHasStringExpandContent(sourceRow: Record<string, unknown>): boolean {
    return this.expandList.some((expand) => {
      const value = sourceRow[expand.name];
      return value != null && value !== "" && !Array.isArray(value);
    });
  }

  private computeIsOddRow(index: number): boolean {
    return this.stripedRows && index % 2 === 1;
  }

  isDataRow = (_index: number, row: DsTableRow): boolean => row.__rowType !== "nested";

  isNestedRow = (_index: number, row: DsTableRow): boolean => row.__rowType === "nested";

  showExpandDetailRow = (_index: number, row: DsTableRow): boolean => !!row.__showExpandDetail;

  tableTrackBy = (index: number, row: DsTableRow): unknown => {
    if (row.__rowType === "nested") {
      const parentKey = this.trackBy ? this.trackBy(index, row.__parentRow) : row.__parentRow;
      return `nested-${String(parentKey)}-${row.__nestedIndex}-${index}`;
    }

    const sourceRow = row.__sourceRow ?? row;
    return this.trackBy ? this.trackBy(index, sourceRow) : index;
  };

  onSortChange(event: unknown): void {
    this.sortChange.emit(event);
  }

  // Clears the Material sort so header arrows return to the unsorted state.
  // [matSortActive] only applies the initial value; user-driven sort stays in MatSort.
  clearSort(): void {
    if (!this.matSort) {
      return;
    }

    this.matSort.active = "";
    this.matSort.direction = "";
    this.matSort._stateChanges.next();
    this.cdr.markForCheck();
  }

  onContentChanged(): void {
    this.contentChanged.emit();
  }
}
